<template>
  <section
    id="contact"
    class="py-20 bg-annapolis-charcoal"
    aria-labelledby="contact-heading"
  >
    <div class="container mx-auto px-6 max-w-2xl">
      <div class="text-center mb-12">
        <h2
          id="contact-heading"
          class="text-4xl md:text-5xl font-bold text-white mb-4"
        >
          Get in Touch
        </h2>
        <p class="text-xl text-gray-400">
          Have questions or want to learn more? We'd love to hear from you.
        </p>
      </div>

      <form
        @submit.prevent="handleSubmit"
        class="bg-annapolis-navy rounded-xl p-8 shadow-xl border border-annapolis-teal/20"
        aria-label="Contact form"
        novalidate
      >
        <div class="space-y-6">
          <!-- Name Field -->
          <div>
            <label for="name" class="block text-gray-300 font-semibold mb-2">
              Name <span aria-label="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              v-model="formData.name"
              @blur="validateName"
              @input="validateName"
              required
              aria-required="true"
              :aria-invalid="nameError ? 'true' : 'false'"
              :aria-describedby="nameError ? 'name-error' : ''"
              class="w-full px-4 py-3 bg-annapolis-charcoal border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-annapolis-teal transition-colors"
              placeholder="Your name"
            />
            <p
              v-if="nameError"
              id="name-error"
              class="mt-2 text-sm text-red-400"
              role="alert"
            >
              {{ nameError }}
            </p>
          </div>

          <!-- Email Field -->
          <div>
            <label for="email" class="block text-gray-300 font-semibold mb-2">
              Email <span aria-label="required">*</span>
            </label>
            <input
              type="email"
              id="email"
              v-model="formData.email"
              @blur="validateEmail"
              @input="validateEmail"
              required
              aria-required="true"
              :aria-invalid="emailError ? 'true' : 'false'"
              :aria-describedby="emailError ? 'email-error' : ''"
              class="w-full px-4 py-3 bg-annapolis-charcoal border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-annapolis-teal transition-colors"
              placeholder="your.email@example.com"
            />
            <p
              v-if="emailError"
              id="email-error"
              class="mt-2 text-sm text-red-400"
              role="alert"
            >
              {{ emailError }}
            </p>
          </div>

          <!-- Company Field (Optional) -->
          <div>
            <label for="company" class="block text-gray-300 font-semibold mb-2">
              Company <span class="sr-only">(optional)</span>
            </label>
            <input
              type="text"
              id="company"
              v-model="formData.company"
              class="w-full px-4 py-3 bg-annapolis-charcoal border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-annapolis-teal transition-colors"
              placeholder="Your company (optional)"
            />
          </div>

          <!-- Message Field -->
          <div>
            <label for="message" class="block text-gray-300 font-semibold mb-2">
              Message <span aria-label="required">*</span>
            </label>
            <textarea
              id="message"
              v-model="formData.message"
              @blur="validateMessage"
              @input="validateMessage"
              required
              aria-required="true"
              :aria-invalid="messageError ? 'true' : 'false'"
              :aria-describedby="messageError ? 'message-error' : ''"
              rows="6"
              class="w-full px-4 py-3 bg-annapolis-charcoal border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-annapolis-teal transition-colors resize-none"
              placeholder="Tell us about your project or inquiry..."
            ></textarea>
            <p
              v-if="messageError"
              id="message-error"
              class="mt-2 text-sm text-red-400"
              role="alert"
            >
              {{ messageError }}
            </p>
          </div>

          <!-- Submit Button -->
          <div>
            <button
              type="submit"
              :disabled="isSubmitting"
              class="w-full px-4 sm:px-6 py-3 bg-annapolis-teal hover:bg-annapolis-teal/90 text-white font-semibold rounded-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none focus:outline-none focus:ring-2 focus:ring-annapolis-teal focus:ring-offset-2 focus:ring-offset-annapolis-navy relative overflow-hidden"
            >
              <span :class="{ 'opacity-0': isSubmitting }">{{ isSubmitting ? 'Sending...' : 'Send Message' }}</span>
              <svg
                v-if="isSubmitting"
                class="absolute inset-0 m-auto w-5 h-5 animate-spin text-white"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                />
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </button>
          </div>

          <!-- Success/Error Messages -->
          <transition name="fade">
            <div
              v-if="submitMessage"
              class="p-4 rounded-lg"
              :class="submitSuccess ? 'bg-green-900/30 border border-green-500/50' : 'bg-red-900/30 border border-red-500/50'"
              role="status"
              aria-live="polite"
            >
              <p :class="submitSuccess ? 'text-green-400' : 'text-red-400'">
                {{ submitMessage }}
              </p>
            </div>
          </transition>
        </div>
      </form>
    </div>
  </section>
</template>

<script setup>
/**
 * ContactSection component handles the contact form with validation and submission
 * @component
 * @example
 * <ContactSection />
 */
defineOptions({
  name: 'ContactSection'
})

import { ref } from 'vue'

/**
 * Form data interface
 * @typedef {Object} FormData
 * @property {string} name - User's name
 * @property {string} email - User's email
 * @property {string} company - User's company (optional)
 * @property {string} message - User's message
 */
const formData = ref({
  name: '',
  email: '',
  company: '',
  message: ''
})

const isSubmitting = ref(false)
const submitSuccess = ref(false)
const submitMessage = ref('')
const nameError = ref('')
const emailError = ref('')
const messageError = ref('')

/**
 * Validates the name field
 * @returns {boolean} - Whether the name is valid
 */
const validateName = () => {
  if (!formData.value.name.trim()) {
    nameError.value = 'Please enter your name'
    return false
  }
  nameError.value = ''
  return true
}

/**
 * Validates the email field
 * @returns {boolean} - Whether the email is valid
 */
const validateEmail = () => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!formData.value.email.trim()) {
    emailError.value = 'Please enter your email address'
    return false
  }
  if (!emailRegex.test(formData.value.email)) {
    emailError.value = 'Please enter a valid email address'
    return false
  }
  emailError.value = ''
  return true
}

/**
 * Validates the message field
 * @returns {boolean} - Whether the message is valid
 */
const validateMessage = () => {
  if (!formData.value.message.trim()) {
    messageError.value = 'Please enter your message'
    return false
  }
  messageError.value = ''
  return true
}

/**
 * Validates the entire form
 * @returns {boolean} - Whether the form is valid
 */
const validateForm = () => {
  const isNameValid = validateName()
  const isEmailValid = validateEmail()
  const isMessageValid = validateMessage()
  return isNameValid && isEmailValid && isMessageValid
}

/**
 * Handles form submission with validation and API call
 */
const handleSubmit = async () => {
  if (!validateForm()) {
    return
  }

  isSubmitting.value = true
  submitMessage.value = ''

  try {
    const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xrboyvwr'

    const response = await fetch(FORMSPREE_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData.value)
    })

    if (!response.ok) {
      throw new Error('Form submission failed')
    }

    submitSuccess.value = true
    submitMessage.value = 'Thank you for your message! We\'ll get back to you soon.'

    // Reset form and errors
    formData.value = {
      name: '',
      email: '',
      company: '',
      message: ''
    }
    nameError.value = ''
    emailError.value = ''
    messageError.value = ''
  } catch (error) {
    submitSuccess.value = false
    submitMessage.value = 'There was an error sending your message. Please try again.'
    console.error('Form submission error:', error)
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .fade-enter-active,
  .fade-leave-active {
    transition: none;
  }
}
</style>
